import React, { useCallback, useState } from 'react';
import {
  ImageDragInBox,
  FileUploadContainer
} from './index.elements';
import theme from 'themes';
import { Typography } from 'components';
import { Accept, useDropzone } from 'react-dropzone';
import { ToastMsg } from 'common/components/Toast';
import Upload from 'icons/indexed-icons/Upload';
import { themeColors } from 'themes';
import { IcoExcel, IcoPdf, IcoZip } from 'icons/indexed-icons/IcoFileExtension';
import { IcoPhoto } from 'icons/indexed-icons/IcoPhoto';
import { FileIcon } from 'icons/indexed-icons';
import { Checkmark } from 'icons/indexed-icons/checkmark';
import X from 'icons/indexed-icons/X';

interface FileUploadProps {
    acceptedTypes: Accept;
    acceptedFileText?: string;
    maxFileSize?: number;
    file?: File | null;
    url?: string | null;
    onFileChange: (file: File | null) => void;
    handleConfirmationModal?: () => void;
    className?: string;
}

export type FileTypeMap = {
    [x: string]: string | JSX.Element;
};

const bytesToMB = (bytes: number) => {
    if (bytes >= 1024 * 1024) {
        const mb = bytes / (1024 * 1024);
        return `${mb.toFixed(2)} MB`;
    } else {
        const kb = bytes / 1024;
        return `${kb.toFixed(2)} KB`;
    }
};

const getFileIcon = (fileName: string): JSX.Element | string => {
    const extension = fileName?.split('.')?.pop()?.toLowerCase() || '';
    
    const fileTypeMap: FileTypeMap = {
        jpg: <IcoPhoto width="30" />,
        jpeg: <IcoPhoto width="30" />,
        png: <IcoPhoto width="30" />,
        pdf: <IcoPdf />,
        xls: <IcoExcel />,
        xlsx: <IcoExcel />,
        csv: <IcoExcel />,
        zip: <IcoZip />
        // Add more mappings as needed
    };
    
    return fileTypeMap[extension] || <FileIcon width="26" />;
};

const FileUpload = ({
    handleConfirmationModal,
    acceptedTypes,
    acceptedFileText,
    maxFileSize,
    onFileChange,
    className = ''
}: FileUploadProps): JSX.Element => {

    const [uploadedFile, setUploadedFile] = useState<File | null>(null);

    const onDrop = useCallback((acceptedFiles: File[]) => {
        if (acceptedFiles.length === 0) {
        ToastMsg({
            heading: 'Wrong format',
            message: 'The file format you selected is not supported.',
            type: 'error',
            position: 'top-right'
        });
        }else if(acceptedFiles[0].size > maxFileSize!){
        ToastMsg({
            heading: 'File size exceeded',
            message: 'The file size you selected is greater than the maximum file size allowed.',
            type: 'error',
            position: 'top-right'
        });
        }
        console.log(acceptedFiles);
        onFileChange(acceptedFiles[0]);
        setUploadedFile(acceptedFiles[0]);
    }, []);

    const { getRootProps, getInputProps, open, isDragAccept } = useDropzone({
        onDrop: onDrop,
        noClick: true,
        accept: acceptedTypes,
        multiple: false,
        maxFiles: 1,
        maxSize: maxFileSize ? maxFileSize : 10485760
    });

    const handleFileDelete = (file: File | null) => {
        setUploadedFile(null);  
        onFileChange(null);
    };

    return (
        <FileUploadContainer className={className}>
            <ImageDragInBox {...getRootProps()} isDragAccept={isDragAccept}>
            <input {...getInputProps()} />
            <div className='drag-area' onClick={open}>
                <div className='upload-icon-wrapper'>
                    <Upload />
                </div>
                <div className='drag-prompt'>
                    <span style={{ color: themeColors.primaryBlue5 }}>Click</span> or drag file here
                </div>
                <div className='file-type-and-size-text'>
                    {acceptedFileText ? acceptedFileText : '.jpg, .png, File size (500 kB max).'}
                </div>
            </div>
            </ImageDragInBox>
            { uploadedFile && (
                <div className='file-area'>
                    {getFileIcon(uploadedFile?.name || '')}
                    <div className='file-info'>
                        <Typography className="fileName" as="span" variant="stepheading">
                            {uploadedFile?.name}
                            <div className='file-size'>{`${uploadedFile?.size ? bytesToMB(uploadedFile.size) : ''}`}</div>
                        </Typography>
                        <div className='file-status'>
                            <Typography
                                className="status"
                                weight="medium"
                                color={theme.colors.green3}
                                mb={0}
                            >
                                {Checkmark({ color: theme.colors.green3 })}
                                {'Done'}
                            </Typography>
                        </div>
                    </div>
                    <div className='delete-icon' onClick={() => handleFileDelete(uploadedFile)}>
                        <X color={theme.colors.error200} />
                    </div>
                </div>
            )}
        </FileUploadContainer>
    );
};

export default FileUpload
